import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllPoints, selectTspOptions } from '../map/slice/mapSelectors';
import {
  useGetRatesQuery,
  useGetTSPsQuery,
  useGetZonesQuery,
} from '../services/edgeApi';
import { Contract, Point, PointFlowDirection } from '@/types';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { PointBadge } from '../helpers/PointBadge';
import { useTheme } from '../helpers/hooks';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import {
  CloseIcon,
  EditIcon,
  PlusCircleIcon,
  PlusIcon,
} from '@iconicicons/react';
import { PointSelectorModal } from '../points/PointSelectorModal';
import { SubmitHandler, Controller, useForm } from 'react-hook-form';
import { IconButton } from '../helpers/IconButton';

export type ContractFormProps = {
  onCreate?: (contract: Contract) => void;
  baseContract?: Partial<Contract>;
};

export const ContractForm: React.FC<ContractFormProps> = ({
  onCreate,
  baseContract,
}) => {
  useGetTSPsQuery(undefined);

  const [theme] = useTheme();
  const points = useSelector(selectAllPoints);

  const [isAddingReceipt, setIsAddingReceipt] = useState(false);
  const [isSelectingReceiptPoint, setIsSelectingReceiptPoint] = useState(false);
  const [newReceiptPoint, setNewReceiptPoint] = useState<{
    point?: Point;
    volume?: number;
  }>({});
  const [isAddingDelivery, setIsAddingDelivery] = useState(false);
  const [isSelectingDeliveryPoint, setIsSelectingDeliveryPoint] =
    useState(false);
  const [newDeliveryPoint, setNewDeliveryPoint] = useState<{
    point?: Point;
    volume?: number;
  }>({});

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isValid, ...formState },
    ...form
  } = useForm<Partial<Contract>>({
    mode: 'onChange',
    defaultValues: baseContract ?? { use_in_scenarios: true },
  });
  const onSubmit: SubmitHandler<Partial<Contract>> = (data) => {
    onCreate?.(data as Contract);
  };

  const formValues = watch();

  const { data: zones } = useGetZonesQuery(
    {
      tsp: formValues.tsp,
    },
    { skip: !formValues.tsp },
  );
  const { data: fuelRates } = useGetRatesQuery(
    {
      tsp: formValues.tsp,
      category: 'FUEL',
    },
    { skip: !formValues.tsp },
  );
  const { data: commodityRates } = useGetRatesQuery(
    {
      tsp: formValues.tsp,
      category: 'COMMODITY',
    },
    { skip: !formValues.tsp },
  );

  const tspOptions = useSelector(selectTspOptions);

  useEffect(() => {
    form.trigger();
  }, []);

  const commodityRateOptions = useMemo(() => {
    if (!formValues.tsp || !commodityRates) return [];
    return commodityRates.map((rate) => ({
      value: rate.id,
      label: rate.name,
    }));
  }, [commodityRates, formValues.tsp]);

  const fuelRateOptions = useMemo(() => {
    if (!formValues.tsp || !fuelRates) return [];
    return fuelRates.map((rate) => ({
      value: rate.id,
      label: rate.name,
    }));
  }, [fuelRates, formValues.tsp]);

  const zoneOptions = useMemo(() => {
    if (!zones) {
      const baseZoneSet = new Set<string>([
        ...(baseContract?.receipt_zones || []),
        ...(baseContract?.delivery_zones || []),
      ]);
      return Array.from(baseZoneSet).map((zoneId) => {
        return {
          value: zoneId,
          label: '',
        };
      });
    }
    return Object.values(zones).map((zone) => ({
      value: zone.id.toString(),
      label: zone.name,
    }));
  }, [zones, formValues.tsp]);

  const addPoint = (
    point: Point | undefined,
    volume: number,
    direction: PointFlowDirection,
  ) => {
    if (!point?.id || !volume) return;
    const volumeKey: 'receipt_volumes' | 'delivery_volumes' =
      direction === 'RECEIPT' ? 'receipt_volumes' : 'delivery_volumes';
    const existingVolumes =
      form.getValues(volumeKey)?.filter((vol) => vol.point_id !== point.id) ||
      [];
    form.setValue(
      volumeKey,
      [...existingVolumes, { point_id: Number(point.id), volume }],
      { shouldValidate: true },
    );
  };

  const removePoint = (pointId: number, direction: PointFlowDirection) => {
    const volumeKey: 'receipt_volumes' | 'delivery_volumes' =
      direction === 'RECEIPT' ? 'receipt_volumes' : 'delivery_volumes';

    const volumes =
      form.getValues(volumeKey)?.filter((vol) => vol.point_id !== pointId) ||
      [];
    form.setValue(volumeKey, volumes, { shouldValidate: true });
  };

  return (
    <>
      <p className="text-muted fs-sm">* Required fields</p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row my-4">
          <div className="col-6">
            <label htmlFor="tsp-form-select" className="form-label">
              Pipeline *
            </label>
            <select
              disabled={Boolean(baseContract)}
              className="form-select"
              id="tsp-form-select"
              defaultValue={undefined}
              {...register('tsp', {
                required: true,
                min: 1,
                valueAsNumber: true,
              })}
            >
              <option value={''}>Select...</option>
              {tspOptions.map((tsp) => (
                <option key={tsp.value} value={tsp.value}>
                  {tsp.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {formValues.tsp ? (
          <>
            <hr />
            <div className="row mt-4">
              <div className="col-6">
                <label htmlFor="k-id-form-input" className="form-label">
                  Contract Number *
                </label>
                <input
                  className={
                    'form-control' + (errors.k_id ? ' is-invalid' : '')
                  }
                  id="k-id-form-input"
                  {...register('k_id', { required: true })}
                />
              </div>
              <div className="col-6">
                <label htmlFor="name-form-input" className="form-label">
                  Contract Name
                </label>
                <input
                  className="form-control"
                  id="name-form-input"
                  {...register('name')}
                />
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-6">
                <label htmlFor="mdq-form-select" className="form-label">
                  Maximum Daily Quantity *
                </label>
                <input
                  type="number"
                  min={1}
                  className={
                    'form-control' +
                    (errors.max_daily_quantity ? ' is-invalid' : '')
                  }
                  id="mdq-form-input"
                  placeholder="Dth"
                  {...register('max_daily_quantity', {
                    required: true,
                    min: 1,
                    valueAsNumber: true,
                  })}
                />
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-6">
                <label htmlFor="start-date-form-input" className="form-label">
                  Start Date *
                </label>
                <input
                  type="date"
                  className={
                    'form-control' + (errors.start_date ? ' is-invalid' : '')
                  }
                  id="start-date-form-input"
                  {...register('start_date', {
                    required: true,
                  })}
                />
              </div>
              <div className="col-6">
                <label htmlFor="end-date-form-input" className="form-label">
                  End Date *
                </label>
                <input
                  type="date"
                  className={
                    'form-control' + (errors.end_date ? ' is-invalid' : '')
                  }
                  id="end-date-form-input"
                  {...register('end_date', {
                    required: true,
                  })}
                />
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-3">
                <label
                  htmlFor="contract-type-form-input"
                  className="form-label"
                >
                  Contract Type *
                </label>
                <select
                  className={'form-select' + (errors.type ? ' is-invalid' : '')}
                  id="contract-type-form-input"
                  defaultValue=""
                  {...register('type', { required: true })}
                >
                  <option value={''}>Select...</option>
                  <option value="FIRM">Firm</option>
                  <option value="IT">IT</option>
                </select>
              </div>
              <div className="col-3">
                <label
                  htmlFor="rate-schedule-form-input"
                  className="form-label"
                >
                  Rate Schedule Type *
                </label>
                <select
                  className={
                    'form-select' +
                    (errors.rate_schedule_type ? ' is-invalid' : '')
                  }
                  id="rate-schedule-form-input"
                  {...register('rate_schedule_type', { required: true })}
                >
                  <option value={''}>Select...</option>
                  <option value="FT">FT</option>
                  <option value="FT_NN">FT_NN</option>
                  <option value="IT">IT</option>
                  <option value="CONTESTING">CONTESTING</option>
                  <option value="SETTLEMENT">SETTLEMENT</option>
                </select>
              </div>
              <div className="col-3">
                <label
                  htmlFor="fuel-rate-schedule-form-input"
                  className="form-label d-flex gap-2"
                >
                  Fuel Rate *{' '}
                  <span className="d-flex gap-1">
                    <a
                      className="text-muted"
                      href={`/contracts/?rate_tsp=${formValues.tsp}&new=true&category=COMMODITY`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconButton icon={PlusCircleIcon} />{' '}
                    </a>
                    {Boolean(formValues.fuel_rate_schedule) && (
                      <a
                        className="text-muted"
                        style={{ fontSize: 8 }}
                        href={`/contracts/?rate_tsp=${formValues.tsp}&rate_schedule=${formValues.fuel_rate_schedule}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconButton icon={EditIcon} />
                      </a>
                    )}
                  </span>
                </label>
                <select
                  className={
                    'form-select' +
                    (errors.fuel_rate_schedule ? ' is-invalid' : '')
                  }
                  id="fuel-rate-schedule-form-input"
                  {...register('fuel_rate_schedule', {
                    required: true,
                    valueAsNumber: true,
                  })}
                >
                  <option value={''}>Select...</option>
                  {baseContract?.fuel_rate_schedule && (
                    <option value={baseContract.fuel_rate_schedule}>
                      {baseContract?.fuel_rate_schedule_name}
                    </option>
                  )}
                  {fuelRateOptions
                    ?.filter(
                      (rate) => rate.value !== baseContract?.fuel_rate_schedule,
                    )
                    .map((rate) => (
                      <option key={rate.value} value={rate.value}>
                        {rate.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-3">
                <label
                  htmlFor="commodity-schedule-form-input"
                  className="form-label d-flex gap-2"
                >
                  Commodity Rate *
                  <span className="d-flex gap-1">
                    <a
                      className="text-muted"
                      href={`/contracts/?rate_tsp=${formValues.tsp}&new=true&category=COMMODITY`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconButton icon={PlusCircleIcon} />{' '}
                    </a>
                    {Boolean(formValues.commodity_rate_schedule) && (
                      <a
                        className="text-muted"
                        style={{ fontSize: 8 }}
                        href={`/contracts/?rate_tsp=${formValues.tsp}&rate_schedule=${formValues.commodity_rate_schedule}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconButton icon={EditIcon} />
                      </a>
                    )}
                  </span>
                </label>
                <select
                  className={
                    'form-select' +
                    (errors.commodity_rate_schedule ? ' is-invalid' : '')
                  }
                  id="commodity-rate-schedule-form-input"
                  {...register('commodity_rate_schedule', {
                    required: true,
                    valueAsNumber: true,
                  })}
                >
                  <option value={''}>Select...</option>
                  {baseContract?.commodity_rate_schedule && (
                    <option value={baseContract.commodity_rate_schedule}>
                      {baseContract?.commodity_rate_schedule_name}
                    </option>
                  )}
                  {commodityRateOptions
                    ?.filter(
                      (rate) =>
                        rate.value !== baseContract?.commodity_rate_schedule,
                    )
                    .map((rate) => (
                      <option key={rate.value} value={rate.value}>
                        {rate.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <label htmlFor="description-form-input" className="form-label">
                  Description
                </label>
                <textarea
                  className="form-control"
                  id="description-form-input"
                  placeholder="Description"
                  {...register('description')}
                  rows={4}
                />
              </div>
            </div>

            {/* TODO: Backend fix needed - ContractView/Serializer should allow empty receipt_zones and delivery_zones 
            See issue 2451*/}
            <div className="row mt-4">
              <div className="col-6">
                <label className="form-label">Receipt Zones *</label>
                <Controller
                  name="receipt_zones"
                  control={control}
                  // Making zones not required for now
                  // rules={{
                  //   validate: {
                  //     required: (v) => Array.isArray(v) && v?.length > 0,
                  //   },
                  // }}
                  render={() => (
                    <MultiSelectSearch
                      options={zoneOptions}
                      disabled={!formValues.tsp}
                      placeholder={'Select Zones'}
                      value={formValues.receipt_zones}
                      onChange={(selected) =>
                        form.setValue(
                          'receipt_zones',
                          selected.map((s) => s.value),
                          { shouldValidate: true },
                        )
                      }
                    />
                  )}
                />
                {errors.receipt_zones && (
                  <span className="text-danger mt-0">
                    Receipt zone is required
                  </span>
                )}
              </div>

              <div className="col-6">
                <label className="form-label">Delivery Zones *</label>
                <Controller
                  name="delivery_zones"
                  control={control}
                  // Making zones not required for now
                  // rules={{
                  //   validate: {
                  //     required: (v) => Array.isArray(v) && v?.length > 0,
                  //   },
                  // }}
                  render={() => (
                    <MultiSelectSearch
                      options={zoneOptions}
                      disabled={!formValues.tsp}
                      placeholder={'Select Zones'}
                      value={formValues.delivery_zones}
                      onChange={(selected) =>
                        form.setValue(
                          'delivery_zones',
                          selected.map((s) => s.value),
                          { shouldValidate: true },
                        )
                      }
                    />
                  )}
                />
                {errors.delivery_zones && (
                  <span className="text-danger mt-0">
                    Delivery zone is required
                  </span>
                )}
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-6">
                <label className="form-label">Receipt Points</label>
                {formValues.receipt_volumes?.map(
                  ({ point_id: pointId, volume }) => (
                    <PointBadge
                      style={{ maxWidth: 'none' }}
                      theme={theme}
                      key={pointId}
                      point={points[pointId]}
                      extra_text={
                        <span>
                          {volume} dth
                          <br />
                          <CloseIcon
                            onClick={(evt) => {
                              evt.stopPropagation();
                              removePoint(Number(pointId), 'RECEIPT');
                            }}
                          />
                        </span>
                      }
                    />
                  ),
                )}
                {isAddingReceipt ? (
                  <div className="input-group">
                    <input
                      disabled={!formValues.tsp}
                      readOnly
                      placeholder="Receipt Point (click to select)"
                      value={
                        newReceiptPoint.point ? newReceiptPoint.point.name : ''
                      }
                      className="form-control"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsSelectingReceiptPoint(true);
                      }}
                    />
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      max={formValues.max_daily_quantity}
                      onChange={(e) =>
                        setNewReceiptPoint((prev) => ({
                          ...prev,
                          volume: Number(e.target.value),
                        }))
                      }
                      placeholder="Volume (dth)"
                    />
                    <button
                      className="btn btn-secondary input-group-text"
                      disabled={
                        !newReceiptPoint.point ||
                        !newReceiptPoint.point.id ||
                        !newReceiptPoint.volume
                      }
                      onClick={() => {
                        setIsAddingReceipt(false);
                        addPoint(
                          newReceiptPoint.point,
                          Number(newReceiptPoint.volume),
                          'RECEIPT',
                        );
                        setNewReceiptPoint({});
                      }}
                    >
                      Add
                    </button>
                  </div>
                ) : (
                  <BadgeDisplay
                    style={{ maxWidth: 'none' }}
                    theme={theme}
                    role="button"
                    onClick={() => setIsAddingReceipt(true)}
                  >
                    <PlusIcon />
                  </BadgeDisplay>
                )}
              </div>
              <div className="col-6">
                <label className="form-label">Delivery Points</label>
                {formValues.delivery_volumes?.map(
                  ({ point_id: pointId, volume }) => (
                    <PointBadge
                      style={{ maxWidth: 'none' }}
                      theme={theme}
                      key={pointId}
                      point={points[pointId]}
                      extra_text={
                        <span>
                          {volume} dth
                          <br />
                          <CloseIcon
                            onClick={(evt) => {
                              evt.stopPropagation();
                              removePoint(Number(pointId), 'DELIVERY');
                            }}
                          />
                        </span>
                      }
                    />
                  ),
                )}
                {isAddingDelivery ? (
                  <div className="input-group">
                    <input
                      readOnly
                      disabled={!formValues.tsp}
                      placeholder="Delivery Point (click to select)"
                      value={
                        newDeliveryPoint.point
                          ? newDeliveryPoint.point.name
                          : ''
                      }
                      className="form-control"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsSelectingDeliveryPoint(true);
                      }}
                    />
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      max={formValues.max_daily_quantity}
                      onChange={(e) =>
                        setNewDeliveryPoint((prev) => ({
                          ...prev,
                          volume: Number(e.target.value),
                        }))
                      }
                      placeholder="Volume (dth)"
                    />
                    <button
                      className="btn btn-secondary input-group-text"
                      disabled={
                        !newDeliveryPoint.point ||
                        !newDeliveryPoint.point.id ||
                        !newDeliveryPoint.volume
                      }
                      onClick={() => {
                        setIsAddingDelivery(false);
                        addPoint(
                          newDeliveryPoint.point,
                          Number(newDeliveryPoint.volume),
                          'DELIVERY',
                        );
                        setNewDeliveryPoint({});
                      }}
                    >
                      Add
                    </button>
                  </div>
                ) : (
                  <BadgeDisplay
                    style={{ maxWidth: 'none' }}
                    theme={theme}
                    role="button"
                    onClick={() => setIsAddingDelivery(true)}
                  >
                    <PlusIcon />
                  </BadgeDisplay>
                )}
              </div>
            </div>

            <div className="mt-3 d-flex justify-content-end">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isValid === false || formState.isSubmitting === true}
              >
                {baseContract ? 'Update Contract' : 'Create Contract'}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-4 alert alert-info text-center">
            Please select a Pipeline to configure the contract details.
          </div>
        )}
      </form>
      <PointSelectorModal
        lockedTsps={formValues.tsp ? [Number(formValues.tsp)] : []}
        isOpen={isSelectingReceiptPoint}
        onClose={() => setIsSelectingReceiptPoint(false)}
        onSelect={(p) => {
          setNewReceiptPoint((prev) => ({ ...prev, point: p }));
          setIsSelectingReceiptPoint(false);
        }}
      />
      <PointSelectorModal
        lockedTsps={formValues.tsp ? [Number(formValues.tsp)] : []}
        isOpen={isSelectingDeliveryPoint}
        onClose={() => setIsSelectingDeliveryPoint(false)}
        onSelect={(p) => {
          setNewDeliveryPoint((prev) => ({ ...prev, point: p }));
          setIsSelectingDeliveryPoint(false);
        }}
      />
    </>
  );
};
